/**
 * Inline question edit form (MCQ / True-False) for the Quiz Editor.
 *
 * Extracted verbatim from QuizEditorPage.jsx during the Phase 3 refactor
 * (no behaviour change).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { optUid } from './quizMeta';

export function QuestionEditForm({ q, idx, onCancel, onSaved, quizId, put }) {
  const [text, setText] = useState(q.question_text);
  const [options, setOptions] = useState(() => (q.options || []).map(o => ({ ...o, _uid: optUid() })));
  const [explanation, setExplanation] = useState(q.explanation || '');
  const [points, setPoints] = useState(q.points ?? 1);
  const [saving, setSaving] = useState(false);

  const setOpt = (i, patch) =>
    setOptions(prev => prev.map((o, idx2) => idx2 === i ? { ...o, ...patch } : o));

  const addOption = () =>
    setOptions(prev => [...prev, { _uid: optUid(), text: '', is_correct: false }]);

  const removeOption = (i) =>
    setOptions(prev => prev.filter((_, idx2) => idx2 !== i));

  const toggleCorrect = (i) => {
    if (q.question_type === 'true_false') {
      // Single-correct: flip the chosen one true, rest false
      setOptions(prev => prev.map((o, idx2) => ({ ...o, is_correct: idx2 === i })));
    } else {
      // MCQ — single correct convention
      setOptions(prev => prev.map((o, idx2) => ({ ...o, is_correct: idx2 === i })));
    }
  };

  const canSave = text.trim().length > 0
    && options.length >= 2
    && options.every(o => o.text.trim().length > 0)
    && options.some(o => o.is_correct);

  const handleSave = async () => {
    setSaving(true);
    try {
      await put(`/quizzes/${quizId}/questions/${q.question_id}`, {
        question_text: text.trim(),
        points: Number(points) || 1,
        explanation: explanation.trim() || null,
        options: options.map(o => ({ text: o.text.trim(), is_correct: !!o.is_correct })),
      });
      toast.success('Question saved');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3" data-testid={`question-edit-${q.question_id}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-mono text-muted-foreground">Editing Q{idx}</p>
        <Badge variant="outline" className="text-[10px] uppercase">{q.question_type.replace('_', ' ')}</Badge>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="qtext">Question</Label>
        <Textarea
          id="qtext"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="min-h-[72px]"
          data-testid="edit-question-text"
        />
      </div>

      <div className="space-y-2">
        <Label>Options · click the circle to mark correct</Label>
        {options.map((o, i) => (
          <div key={o._uid} className="flex items-center gap-2" data-testid={`edit-option-row-${i}`}>
            <button
              type="button"
              onClick={() => toggleCorrect(i)}
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                o.is_correct ? 'border-emerald-500 bg-emerald-500' : 'border-muted-foreground/40'
              }`}
              title={o.is_correct ? 'Correct answer' : 'Mark correct'}
              data-testid={`edit-option-correct-${i}`}
            >
              {o.is_correct && <CheckCircle2 className="w-3 h-3 text-white" />}
            </button>
            <Input
              value={o.text}
              onChange={(e) => setOpt(i, { text: e.target.value })}
              placeholder={`Option ${i + 1}`}
              data-testid={`edit-option-text-${i}`}
            />
            {options.length > 2 && q.question_type !== 'true_false' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeOption(i)}
                className="text-red-500"
                data-testid={`edit-option-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
        {q.question_type !== 'true_false' && options.length < 6 && (
          <Button variant="ghost" size="sm" onClick={addOption} data-testid="edit-add-option">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add option
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="qpoints">Points</Label>
          <Input
            id="qpoints"
            type="number"
            min={0}
            step={0.5}
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            data-testid="edit-points"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="qexpl">Explanation (optional)</Label>
        <Textarea
          id="qexpl"
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          placeholder="Why is this the correct answer?"
          data-testid="edit-explanation"
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={saving} data-testid="edit-cancel">Cancel</Button>
        <Button onClick={handleSave} disabled={!canSave || saving} data-testid="edit-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
          Save question
        </Button>
      </div>
    </div>
  );
}
